import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPassword, resetPassword } from '../redux/slices/authSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/Auth.css';

const ForgotPasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, successMessage } = useSelector((state) => state.auth);
  const resetToken = searchParams.get('token');

  const [step, setStep] = useState(resetToken ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      alert('Please enter your email address');
      return;
    }

    const result = await dispatch(forgotPassword(email));
    if (result.type === forgotPassword.fulfilled.type) {
      setStep('check-email');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();

    if (!formData.newPassword || !formData.confirmPassword) {
      alert('Please fill in all fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    const result = await dispatch(
      resetPassword({
        token: resetToken,
        newPassword: formData.newPassword,
      })
    );

    if (result.type === resetPassword.fulfilled.type) {
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {step === 'request' && (
          <>
            <h1 className="auth-title">Reset Password</h1>
            <p className="auth-subtitle">
              Enter your email address to receive a password reset link
            </p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleEmailSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  disabled={loading}
                />
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}

        {step === 'check-email' && (
          <>
            <h1 className="auth-title">Check Your Email</h1>
            <p className="auth-subtitle">
              We've sent a password reset link to your email address. Check your inbox and click the
              link to reset your password.
            </p>

            <div className="success-message" style={{ marginBottom: '20px' }}>
              {successMessage}
            </div>

            <button
              onClick={() => navigate(-1)}
              className="auth-button"
              style={{ marginTop: '20px' }}
            >
              ← Return
            </button>
          </>
        )}

        {step === 'reset' && (
          <>
            <h1 className="auth-title">Reset Your Password</h1>
            <p className="auth-subtitle">Enter your new password below</p>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <form onSubmit={handleResetSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password (min 6 characters)"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your new password"
                  disabled={loading}
                />
              </div>

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        <div className="auth-footer">
          <p>
            Remember your password? <a href="/login">Login here</a>
          </p>
        </div>

        {/* Privacy Policy Footer */}
        <div className="privacy-footer-link">
          <p className="privacy-text">
            By using our service, you agree to our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
